/**
 * TDBPnLTrendSparkline — Month-over-month margin % trend for TDB.
 *
 * Reads `GET /api/tdb/mis/trend?doctrine=scaleboard|source`.
 *
 * Plots margin % (NOT absolute GOP — absolute moves with seasonality,
 * margin % is the operational health signal) across all available v4
 * MIS periods. Group + 3 city lines on one chart. Red zero-line drawn
 * so above-line / below-line is readable at a glance.
 *
 * Gated on ≥3 v4 periods. Below threshold renders a placeholder card
 * showing "currently N period loaded — trend lights up at 3".
 */
import { useEffect, useState } from 'react';
import { TrendingUp, Sparkles } from 'lucide-react';
import api from '@/lib/api';

const CITY_COLOR = {
  Bengaluru: '#14532D',  // emerald-900
  Mumbai:    '#1E40AF',  // blue-800
  Gurgaon:   '#991B1B',  // red-800
  group:     '#111827',  // gray-900 (bold)
};

const W = 720;     // chart viewbox width
const H = 160;     // chart viewbox height
const PADDING = { top: 18, right: 28, bottom: 28, left: 36 };

function monthShort(ym) {
  if (!ym) return '';
  const [y, m] = ym.split('-');
  const d = new Date(Date.UTC(+y, +m - 1, 1));
  return d.toLocaleDateString('en-IN', { month: 'short' });
}

function buildPath(points, accessor, x0, dx, y0, yScale) {
  if (!points || points.length === 0) return '';
  return points
    .map((p, i) => {
      const x = x0 + i * dx;
      const y = y0 - accessor(p) * yScale;
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
}

export default function TDBPnLTrendSparkline({ doctrine = 'scaleboard' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get('/tdb/mis/trend', {
          params: { doctrine, min_periods: 3 },
        });
        if (!cancelled) setData(r.data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [doctrine]);

  if (loading) {
    return (
      <div
        data-testid="tdb-pnl-trend-loading"
        className="mt-3 p-4 rounded-xl bg-gray-50 border border-gray-200 text-xs text-gray-500"
      >
        Loading trend…
      </div>
    );
  }

  // Below ≥3 periods → placeholder
  if (!data?.available) {
    const loaded = data?.loaded_periods || 0;
    return (
      <div
        data-testid="tdb-pnl-trend-placeholder"
        className="mt-3 p-4 rounded-xl bg-indigo-50 border border-indigo-200"
      >
        <div className="flex items-start gap-2">
          <Sparkles className="w-4 h-4 text-indigo-700 mt-0.5 shrink-0" />
          <div className="text-xs text-indigo-900">
            <div className="font-semibold mb-0.5">Margin % trend · ready at 3 periods</div>
            <div>
              Currently <span className="font-medium">{loaded} v4 period{loaded === 1 ? '' : 's'}</span> loaded.
              {loaded < 3 ? ` ${3 - loaded} more month${3 - loaded === 1 ? '' : 's'} of MIS data` : ''} and the sparkline lights up automatically — no redeploy.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const points = data.points || [];

  // Y range: include all city + group margin pcts, ensure 0% is always visible
  const allPcts = [];
  for (const p of points) {
    if (p.group) allPcts.push(p.group.gop_pct);
    for (const c of Object.values(p.by_city || {})) allPcts.push(c.gop_pct);
  }
  allPcts.push(0);
  const yMin = Math.min(...allPcts);
  const yMax = Math.max(...allPcts);
  const yRange = Math.max(yMax - yMin, 1);

  const innerW = W - PADDING.left - PADDING.right;
  const innerH = H - PADDING.top - PADDING.bottom;
  const dx = points.length > 1 ? innerW / (points.length - 1) : 0;
  const x0 = PADDING.left;
  const y0 = PADDING.top + innerH * (yMax / yRange);
  const yScale = innerH / yRange;

  const groupPath  = buildPath(points, (p) => p.group?.gop_pct || 0, x0, dx, y0, yScale);
  const blrPath    = buildPath(points, (p) => p.by_city?.Bengaluru?.gop_pct || 0, x0, dx, y0, yScale);
  const mumPath    = buildPath(points, (p) => p.by_city?.Mumbai?.gop_pct || 0, x0, dx, y0, yScale);
  const gurPath    = buildPath(points, (p) => p.by_city?.Gurgaon?.gop_pct || 0, x0, dx, y0, yScale);

  // Zero-line y position
  const zeroY = y0;

  return (
    <div
      data-testid="tdb-pnl-trend-sparkline"
      className="mt-3 p-4 rounded-xl bg-white border border-gray-200"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-gray-700" />
          <span className="text-sm font-semibold text-gray-900">
            Margin % trend
          </span>
          <span className="text-xs text-gray-500">
            · {points.length} period{points.length === 1 ? '' : 's'}
          </span>
        </div>
        <div className="flex items-center gap-3 text-[10px] text-gray-600">
          {Object.entries(CITY_COLOR).filter(([k]) => k !== 'group').map(([city, color]) => (
            <div key={city} className="flex items-center gap-1">
              <span className="w-2.5 h-0.5" style={{ backgroundColor: color }} />
              {city.slice(0, 3)}
            </div>
          ))}
          <div className="flex items-center gap-1 font-medium text-gray-800">
            <span className="w-2.5 h-0.5" style={{ backgroundColor: CITY_COLOR.group }} />
            Group
          </div>
        </div>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full" style={{ height: 160 }}>
        {/* Y axis labels (top + bottom + 0) */}
        <text x={PADDING.left - 4} y={PADDING.top} fontSize="9" textAnchor="end" fill="#6B7280">{yMax.toFixed(0)}%</text>
        <text x={PADDING.left - 4} y={H - PADDING.bottom + 4} fontSize="9" textAnchor="end" fill="#6B7280">{yMin.toFixed(0)}%</text>
        <text x={PADDING.left - 4} y={zeroY + 3} fontSize="9" textAnchor="end" fill="#991B1B">0%</text>

        {/* Zero gridline (break-even) */}
        <line
          data-testid="tdb-pnl-trend-zero-line"
          x1={PADDING.left} y1={zeroY}
          x2={W - PADDING.right} y2={zeroY}
          stroke="#FCA5A5" strokeWidth="1" strokeDasharray="4 3"
        />

        {/* City lines */}
        <path d={blrPath} fill="none" stroke={CITY_COLOR.Bengaluru} strokeWidth="1.5" opacity="0.85" />
        <path d={mumPath} fill="none" stroke={CITY_COLOR.Mumbai} strokeWidth="1.5" opacity="0.85" />
        <path d={gurPath} fill="none" stroke={CITY_COLOR.Gurgaon} strokeWidth="1.5" opacity="0.85" />

        {/* Group line on top, bold */}
        <path d={groupPath} fill="none" stroke={CITY_COLOR.group} strokeWidth="2.5" />

        {/* Data dots on group line */}
        {points.map((p, i) => (
          <g key={p.month_ym}>
            <circle
              cx={x0 + i * dx}
              cy={y0 - (p.group?.gop_pct || 0) * yScale}
              r="3"
              fill={CITY_COLOR.group}
            />
            <text
              x={x0 + i * dx}
              y={H - PADDING.bottom + 14}
              fontSize="10"
              textAnchor="middle"
              fill="#6B7280"
            >
              {monthShort(p.month_ym)}
            </text>
          </g>
        ))}
      </svg>

      <div className="mt-2 text-[10px] text-gray-500">
        Margin % over absolute GOP — seasonality hidden, operational drift visible. Red dotted line = break-even.
      </div>
    </div>
  );
}
